import { useEffect, useState, type FormEvent } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Button } from '../../shared/components/Button'
import { previewDateChange, applyDateChange, type DateChangePreview } from '../../shared/api/dateChange'
import type { Trip } from '../../shared/api/types'
import { tripKeys } from './useTrips'
import { pointKeys } from '../timeline/usePoints'
import { useUpdateTrip } from './useTrips'

export function TripEditPanel({ trip, onClose }: { trip: Trip; onClose: () => void }) {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const update = useUpdateTrip(trip.id)
  const [title, setTitle] = useState(trip.title)
  const [description, setDescription] = useState(trip.description)
  const [cover, setCover] = useState(trip.cover_url ?? '')
  const [zone, setZone] = useState(trip.time_zone_id ?? 'Etc/UTC')
  const [start, setStart] = useState(trip.start_date ?? '')
  const [end, setEnd] = useState(trip.end_date ?? '')
  const [preview, setPreview] = useState<DateChangePreview | null>(null)
  const [error, setError] = useState('')
  useEffect(() => { setPreview(null) }, [start, end])

  async function submit(e: FormEvent) {
    e.preventDefault(); setError('')
    if (!title.trim() || !start || !end || end < start) { setError(t('trip_edit.invalid')); return }
    try {
      if (start !== trip.start_date || end !== trip.end_date) {
        setPreview(await previewDateChange(trip.id, start, end))
      } else {
        await update.mutateAsync({ title: title.trim(), description, cover_url: cover || null, time_zone_id: zone })
        onClose()
      }
    } catch (err) { setError(err instanceof Error ? err.message : String(err)) }
  }

  async function apply(out_of_range: 'cancel' | 'keep_unscheduled') {
    if (!preview) return
    try {
      await applyDateChange(trip.id, preview, out_of_range)
      await update.mutateAsync({ title: title.trim(), description, cover_url: cover || null, time_zone_id: zone })
      await Promise.all([qc.invalidateQueries({queryKey:tripKeys.detail(trip.id)}), qc.invalidateQueries({queryKey:pointKeys.byTrip(trip.id)})])
      onClose()
    } catch (err) { setPreview(null); setError(err instanceof Error ? err.message : String(err)) }
  }

  return <div role="dialog" aria-modal="true" aria-label={t('trip_edit.title')} className="fixed inset-0 z-50 overflow-y-auto bg-black/50 p-4">
    <form onSubmit={e => void submit(e)} className="mx-auto mt-8 max-w-xl space-y-3 rounded-st-lg bg-st-surface p-5 shadow-xl">
      <h2 className="text-xl font-semibold">{t('trip_edit.title')}</h2>
      <label className="block text-sm">{t('trip_edit.name')}<input className="mt-1 w-full rounded border border-st-line bg-st-surface p-2" value={title} onChange={e=>setTitle(e.target.value)} /></label>
      <label className="block text-sm">{t('trip_edit.description')}<textarea className="mt-1 w-full rounded border border-st-line bg-st-surface p-2" value={description} onChange={e=>setDescription(e.target.value)} /></label>
      <label className="block text-sm">{t('trip_edit.cover')}<input className="mt-1 w-full rounded border border-st-line bg-st-surface p-2" type="url" value={cover} onChange={e=>setCover(e.target.value)} /></label>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <label className="text-sm">{t('trip_edit.start')}<input className="mt-1 w-full rounded border border-st-line bg-st-surface p-2" type="date" value={start} onChange={e=>setStart(e.target.value)} /></label>
        <label className="text-sm">{t('trip_edit.end')}<input className="mt-1 w-full rounded border border-st-line bg-st-surface p-2" type="date" value={end} onChange={e=>setEnd(e.target.value)} /></label>
      </div>
      <label className="block text-sm">{t('trip_edit.zone')}<input className="mt-1 w-full rounded border border-st-line bg-st-surface p-2" value={zone} onChange={e=>setZone(e.target.value)} /></label>
      {error && <p role="alert" className="text-sm text-st-danger">{error}</p>}
      {preview && <div className="rounded border border-st-line p-3 text-sm">
        <p className="font-semibold">{t('trip_edit.preview')}</p>
        <p>{t('trip_edit.affected', {count:preview.impacts.length})}</p>
        {preview.impacts.filter(i=>i.outside).map(i=><p key={i.point_id} className="text-st-danger">{i.title}: {t('trip_edit.outside')}</p>)}
        {preview.impacts.some(i=>i.fixed_date && !i.outside) && <p>{t('trip_edit.fixed')}</p>}
        <div className="mt-3 flex flex-wrap gap-2">
          <Button type="button" variant="accent" onClick={()=>void apply(preview.has_conflicts ? 'keep_unscheduled' : 'cancel')}>{preview.has_conflicts ? t('trip_edit.keep_unscheduled') : t('trip_edit.apply')}</Button>
          <Button type="button" variant="ghost" onClick={()=>setPreview(null)}>{t('trip_edit.cancel')}</Button>
        </div>
      </div>}
      <div className="flex gap-2"><Button type="submit" variant="primary" disabled={update.isPending}>{t('trip_edit.save')}</Button>
        <Button type="button" variant="ghost" onClick={onClose}>{t('trip_edit.cancel')}</Button></div>
    </form>
  </div>
}
